import { useEffect } from "react";

/**
 * Lightweight per-route SEO — no external lib.
 * Sets <title>, meta description, canonical + Open Graph title/desc/url/image.
 * Restores previous values on unmount so navigation feels correct.
 *
 * Note: this is client-side only. Google now executes JS to index, so these
 * meta tags ARE picked up (verified for Googlebot since 2019). For full
 * SSR/SSG rendering, migrate to Next.js.
 */
export function useDocumentMeta({ title, description, canonical, image, imageAlt, noindex = false, type }) {
  useEffect(() => {
    const prev = {
      title: document.title,
      description: document.querySelector('meta[name="description"]')?.getAttribute("content"),
      canonical: document.querySelector('link[rel="canonical"]')?.getAttribute("href"),
      ogTitle: document.querySelector('meta[property="og:title"]')?.getAttribute("content"),
      ogDesc: document.querySelector('meta[property="og:description"]')?.getAttribute("content"),
      ogUrl: document.querySelector('meta[property="og:url"]')?.getAttribute("content"),
      ogImage: document.querySelector('meta[property="og:image"]')?.getAttribute("content"),
      ogImageSecure: document.querySelector('meta[property="og:image:secure_url"]')?.getAttribute("content"),
      ogImageAlt: document.querySelector('meta[property="og:image:alt"]')?.getAttribute("content"),
      twImage: document.querySelector('meta[name="twitter:image"]')?.getAttribute("content"),
      robots: document.querySelector('meta[name="robots"]')?.getAttribute("content"),
      googlebot: document.querySelector('meta[name="googlebot"]')?.getAttribute("content"),
      ogType: document.querySelector('meta[property="og:type"]')?.getAttribute("content"),
    };
    // Articles: og:type=article and no hardcoded image size (covers have varying ratios).
    const dims = type === "article"
      ? [...document.querySelectorAll('meta[property="og:image:width"], meta[property="og:image:height"]')]
      : [];

    const setMeta = (selector, attr, value) => {
      if (value == null) return;
      let el = document.querySelector(selector);
      if (!el) {
        const [tag, prop] = selector.startsWith("meta[property=")
          ? ["meta", selector.match(/property="([^"]+)"/)?.[1]]
          : selector.startsWith("meta[name=")
          ? ["meta", selector.match(/name="([^"]+)"/)?.[1]]
          : selector.startsWith("link[rel=")
          ? ["link", selector.match(/rel="([^"]+)"/)?.[1]]
          : [null, null];
        if (!tag || !prop) return;
        el = document.createElement(tag);
        if (tag === "meta") {
          if (selector.startsWith("meta[property=")) el.setAttribute("property", prop);
          else el.setAttribute("name", prop);
        } else {
          el.setAttribute("rel", prop);
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    if (title) document.title = title;
    if (type) setMeta('meta[property="og:type"]', "content", type);
    dims.forEach((el) => el.remove());
    if (description) setMeta('meta[name="description"]', "content", description);
    if (canonical) setMeta('link[rel="canonical"]', "href", canonical);
    if (title) setMeta('meta[property="og:title"]', "content", title);
    if (description) setMeta('meta[property="og:description"]', "content", description);
    if (canonical) setMeta('meta[property="og:url"]', "content", canonical);
    if (image) {
      setMeta('meta[property="og:image"]', "content", image);
      setMeta('meta[property="og:image:secure_url"]', "content", image);
      setMeta('meta[name="twitter:image"]', "content", image);
      // Force a large-card layout on Twitter/X + explicit alt (accessibility + SEO)
      setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
      if (imageAlt) setMeta('meta[property="og:image:alt"]', "content", imageAlt);
    }

    // noindex: private / duplicate pages (admin, v2 variants) should never
    // appear in search engines. Overrides the shell defaults.
    if (noindex) {
      setMeta('meta[name="robots"]', "content", "noindex, nofollow");
      setMeta('meta[name="googlebot"]', "content", "noindex, nofollow");
    }

    return () => {
      // Restore on unmount so back-nav shows the correct page's meta
      if (prev.title) document.title = prev.title;
      if (prev.description) setMeta('meta[name="description"]', "content", prev.description);
      if (prev.canonical) setMeta('link[rel="canonical"]', "href", prev.canonical);
      if (prev.ogTitle) setMeta('meta[property="og:title"]', "content", prev.ogTitle);
      if (prev.ogDesc) setMeta('meta[property="og:description"]', "content", prev.ogDesc);
      if (prev.ogUrl) setMeta('meta[property="og:url"]', "content", prev.ogUrl);
      if (prev.ogImage) setMeta('meta[property="og:image"]', "content", prev.ogImage);
      if (prev.ogImageSecure) setMeta('meta[property="og:image:secure_url"]', "content", prev.ogImageSecure);
      if (prev.ogImageAlt) setMeta('meta[property="og:image:alt"]', "content", prev.ogImageAlt);
      if (prev.twImage) setMeta('meta[name="twitter:image"]', "content", prev.twImage);
      if (prev.robots) setMeta('meta[name="robots"]', "content", prev.robots);
      if (prev.googlebot) setMeta('meta[name="googlebot"]', "content", prev.googlebot);
      if (prev.ogType) setMeta('meta[property="og:type"]', "content", prev.ogType);
      dims.forEach((el) => document.head.appendChild(el));
    };
  }, [title, description, canonical, image, imageAlt, noindex, type]);
}
